import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShopError, api, type DeliveryOptions, type PromotionsPreview } from '../api';
import { formatCzk } from '../money';
import { useMoney } from '../currency';
import { useT } from '../i18n';
import { useCart } from '../cart';
import { usePageMeta } from '../seo';
import { track } from '../tracking/tracker';
import { LayerSlot } from '../layers/LayerSlot';

/**
 * MIN/MAX OBJEDNATELNÉ MNOŽSTVÍ (#23) VE VÝLOZE.
 *
 * Server posílá limity v `delivery-options` jako pole `cart.orderLimits`
 * (`shop/checkout.ts`, `cartShippingBasis`). Typ `DeliveryOptions` v `../api`
 * o něm zatím neví a ten soubor patří jinam, takže se pole čte
 * STRUKTURÁLNĚ — a když nedorazí, stránka se chová přesně jako dřív.
 */
interface CartOrderLimit {
  readonly variantId: string;
  readonly minQuantity: number;
  readonly maxQuantity: number | null;
  readonly productName: string;
  readonly variantName: string;
}

function orderLimitsOf(options: DeliveryOptions | null): ReadonlyMap<string, CartOrderLimit> {
  const raw = (options?.cart as { orderLimits?: unknown } | undefined)?.orderLimits;
  if (!Array.isArray(raw)) return new Map();
  const map = new Map<string, CartOrderLimit>();
  for (const entry of raw as readonly Partial<CartOrderLimit>[]) {
    if (typeof entry?.variantId !== 'string') continue;
    if (!Number.isInteger(entry.minQuantity)) continue;
    map.set(entry.variantId, {
      variantId: entry.variantId,
      minQuantity: entry.minQuantity as number,
      maxQuantity: typeof entry.maxQuantity === 'number' ? entry.maxQuantity : null,
      productName: typeof entry.productName === 'string' ? entry.productName : '',
      variantName: typeof entry.variantName === 'string' ? entry.variantName : '',
    });
  }
  return map;
}

/**
 * Hláška k jednomu řádku, nebo `null` když je v pořádku.
 *
 * Musí říct, CO je špatně a CO s tím – ne jen „neplatné množství". Stejné
 * znění jako na serveru (`catalog/order-limits.ts`), aby zákazník po odeslání
 * nedostal jinou větu o tomtéž.
 */
type Translate = (key: string, vars?: Record<string, string | number>) => string;

function violationFor(
  variantId: string,
  quantity: number,
  limits: ReadonlyMap<string, CartOrderLimit>,
  t: Translate,
): string | null {
  const limit = limits.get(variantId);
  if (limit === undefined) return null;
  const what = `${limit.productName} – ${limit.variantName}`;
  if (quantity < limit.minQuantity) {
    return t('qty.cartMin', { what, min: limit.minQuantity });
  }
  if (limit.maxQuantity !== null && quantity > limit.maxQuantity) {
    return t('qty.cartMax', { what, max: limit.maxQuantity });
  }
  return null;
}

export function CartPage() {
  usePageMeta({ title: 'Košík | Super Krmivo', index: false });
  const { t } = useT();
  const { format, isConverted } = useMoney();
  const { lines, count, setQuantity, remove, estimatedTotalMinor } = useCart();
  const [options, setOptions] = useState<DeliveryOptions | null>(null);
  const [promo, setPromo] = useState<PromotionsPreview | null>(null);
  const [error, setError] = useState<string | null>(null);

  // CDP: jednou per mount – zmeny mnozstvi uvnitr stranky uz mereni kosiku
  // pokryvaji cart_item_* eventy.
  useEffect(() => {
    track('cart_viewed', { item_count: count, total_minor: estimatedTotalMinor });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Souhrn ze serveru je zavazny – ceny v kosiku mohly mezitim zestarnout.
  useEffect(() => {
    if (lines.length === 0) {
      setOptions(null);
      return;
    }
    api
      .deliveryOptions(lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity })))
      .then((result) => {
        setOptions(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        setError(caught instanceof ShopError ? caught.message : 'Souhrn se nepodařilo načíst.');
      });
  }, [lines]);

  // Nahled automatickych akci. Debounce ~300 ms + zruseni predchoziho volani
  // (cleanup zahodi zastaraly vysledek). FAIL-SILENT: vypadek jen skryje akce,
  // nikdy nerozbije kosik – castky vzdy ze serveru, klient nic nepocita.
  useEffect(() => {
    if (lines.length === 0) {
      setPromo(null);
      return;
    }
    let cancelled = false;
    const items = lines.map((line) => ({ variantId: line.variantId, quantity: line.quantity }));
    const timer = setTimeout(() => {
      api
        .fetchPromotions(items)
        .then((result) => {
          if (!cancelled) setPromo(result);
        })
        .catch(() => {
          if (!cancelled) setPromo(null);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [lines]);

  if (lines.length === 0) {
    return (
      <>
        <h1>{t('cart.title')}</h1>
        <p className="empty muted">{t('cart.emptyMsg')}</p>
        <Link className="button primary" to="/">{t('cart.backToShop')}</Link>
      </>
    );
  }

  // MIN/MAX OBJEDNATELNÉ MNOŽSTVÍ (#23) – ze SERVERU, ne z košíku.
  //
  // `/api/shop/delivery-options` vrací limity z téže funkce, jakou používá
  // závazná pokladna (`catalog/order-limits.ts`), a počítá je ze SOUČTU přes
  // variantu — takže co tu vidíš, to pokladna uzná. Košík v localStorage by
  // stejné číslo tvrdit nemohl: přepíše ho kdokoli a starší řádky ho neznají.
  //
  // Než odpověď dorazí (nebo když spadne), limity prostě nejsou a stránka se
  // chová jako dřív; rozhodnout stejně musí server.
  const limits = orderLimitsOf(options);
  const blocking = lines
    .map((line) => violationFor(line.variantId, line.quantity, limits, t))
    .filter((message): message is string => message !== null);

  const itemsTotal = options?.cart.itemsTotalMinor ?? estimatedTotalMinor;
  const priceChanged = options !== null && options.cart.itemsTotalMinor !== estimatedTotalMinor;
  const cheapest = options?.shipping.reduce<number | null>(
    (min, offer) => (min === null || offer.priceMinor < min ? offer.priceMinor : min),
    null,
  );

  return (
    <>
      <h1>{t('cart.title')}</h1>

      {error !== null && <p className="alert">{error}</p>}
      {priceChanged && <p className="notice">{t('cart.priceChanged')}</p>}

      <div className="card table-card">
        <table className="table">
          <thead>
            <tr>
              <th>{t('cart.colGoods')}</th>
              <th className="num">{t('cart.colQty')}</th>
              <th className="num">{t('cart.colTotal')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {lines.map((line) => {
              const limit = limits.get(line.variantId);
              const problem = violationFor(line.variantId, line.quantity, limits, t);
              return (
              <tr key={line.variantId}>
                <td>
                  <Link className="link" to={`/produkt/${line.slug}`}>
                    {line.productName}
                  </Link>
                  <div className="muted small">{line.variantName}</div>
                  {/* Limit se ukazuje JEN když nějaký je – u drtivé většiny
                      katalogu žádný není a prázdná věta by jen zabírala místo. */}
                  {limit !== undefined && (
                    <div className={problem === null ? 'muted small' : 'alert small'}>
                      {problem ??
                        (limit.maxQuantity === null
                          ? t('qty.minInfo', { min: limit.minQuantity })
                          : limit.minQuantity > 1
                            ? t('qty.rangeInfo', { min: limit.minQuantity, max: limit.maxQuantity })
                            : t('qty.maxInfo', { max: limit.maxQuantity }))}
                    </div>
                  )}
                </td>
                <td className="num">
                  <input
                    type="number"
                    className="qty"
                    min={limit?.minQuantity ?? 1}
                    // Strop je minimum ze skladu a z limitu – co není skladem,
                    // se neobjedná, i kdyby to limit dovoloval.
                    max={
                      limit?.maxQuantity === undefined || limit.maxQuantity === null
                        ? line.available
                        : Math.min(line.available, limit.maxQuantity)
                    }
                    value={line.quantity}
                    aria-label={`Počet – ${line.productName}`}
                    aria-invalid={problem !== null}
                    onChange={(event) => setQuantity(line.variantId, Number(event.target.value))}
                  />
                </td>
                <td className="num">{format(line.priceMinor * line.quantity)}</td>
                <td className="num">
                  <button
                    type="button"
                    className="link-button"
                    onClick={() => remove(line.variantId)}
                  >
                    {t('cart.remove')}
                  </button>
                </td>
              </tr>
              );
            })}
            {/* Darky pridava server – radek objednavky za plnou cenu, proti
                nemu sleva ve vysi cele ceny. Zakaznik ho nemuze odebrat ani
                menit mnozstvi. */}
            {promo?.gifts.map((gift) => (
              <tr key={`gift-${gift.variantId}`} className="gift-row">
                <td>
                  <span className="gift-name">
                    <span className="tag promo">{t('promo.gift')}</span>
                    {gift.productName}
                  </span>
                  <div className="muted small">{gift.variantName}</div>
                </td>
                <td className="num">{gift.quantity}</td>
                <td className="num">
                  <s className="muted">{format(gift.unitPriceMinor * gift.quantity)}</s>{' '}
                  <span className="gift-free">{t('promo.giftFree')}</span>
                </td>
                <td />
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="grand-total">
              <td colSpan={2}>{t('cart.goodsTotal')}</td>
              <td className="num">{format(itemsTotal)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {promo !== null && promo.promotions.length > 0 && (
        <div className="card promo-summary">
          <h2 className="promo-heading">{t('promo.title')}</h2>
          <ul className="promo-list">
            {promo.promotions.map((applied, index) => (
              <li key={`${applied.name}-${index}`}>
                <span>{applied.name}</span>
                <span className="promo-amount">− {format(applied.discountMinor)}</span>
              </li>
            ))}
          </ul>
          {promo.promoDiscountMinor > 0 && (
            <p className="promo-saved">
              {t('promo.saved', { amount: format(promo.promoDiscountMinor) })}
            </p>
          )}
        </div>
      )}

      {isConverted && (
        <p className="muted small">
          {t('cart.settlement', { amount: formatCzk(itemsTotal) })}
        </p>
      )}

      {cheapest !== null && cheapest !== undefined && (
        <p className="muted small">
          {cheapest === 0
            ? t('cart.freeShip')
            : t('cart.shipFrom', { price: format(cheapest) })}
        </p>
      )}

      {/* Weblayer nad souhrnem (napr. doporuceni). Ve vychozim placementu je
          kosik potlaceny – vrstva mireni sem si musi suppressOnCheckout vypnout. */}
      <LayerSlot name="cart_above_summary" />

      {/* Košík mimo min/max se do pokladny nepustí. Není to ochrana (tou je
          `checkout()` na serveru), je to slušnost: nechat člověka vyplnit
          celou adresu a teprve pak mu objednávku odmítnout je horší než mu to
          říct tady. */}
      {blocking.length > 0 && (
        <ul className="alert" role="alert">
          {blocking.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}

      <div className="actions">
        <Link className="button ghost" to="/">{t('cart.continueShopping')}</Link>
        {blocking.length > 0 ? (
          <span className="button primary disabled" aria-disabled="true">
            {t('cart.checkout')}
          </span>
        ) : (
          <Link className="button primary" to="/pokladna">{t('cart.checkout')}</Link>
        )}
      </div>
    </>
  );
}
